export type CopyResult = "copied" | "unavailable" | "failed";

export interface ClipboardLike {
  writeText(text: string): Promise<void>;
  /** Optional: without it, or when reading is denied, the wipe is skipped instead of clobbering user content. */
  readText?(): Promise<string>;
}

export interface SecretClipboardOptions {
  readonly clipboard: ClipboardLike | undefined;
  /** Fixed window, or a getter so a user preference applies to the next copy. */
  readonly clearAfterMs: number | (() => number);
  readonly schedule: (fn: () => void, ms: number) => number;
  readonly cancel: (id: number) => void;
}

/**
 * Copies secrets and wipes the clipboard after a window. A newer copy restarts the window,
 * so the wipe always follows the most recent secret.
 */
export class SecretClipboard {
  private readonly options: SecretClipboardOptions;
  private timer: number | undefined;
  private copied: string | undefined;

  public constructor(options: SecretClipboardOptions) {
    this.options = options;
  }

  public get clearAfterMs(): number {
    const window = this.options.clearAfterMs;
    return typeof window === "number" ? window : window();
  }

  public async copy(text: string): Promise<CopyResult> {
    const clipboard = this.options.clipboard;
    if (clipboard === undefined) {
      return "unavailable";
    }
    try {
      await clipboard.writeText(text);
    } catch {
      return "failed";
    }
    this.cancelPending();
    this.copied = text;
    this.timer = this.options.schedule((): void => {
      this.timer = undefined;
      void this.wipe();
    }, this.clearAfterMs);
    return "copied";
  }

  /** Wipes now if a secret is still inside its window (e.g. on lock). Leaves foreign clipboard content alone. */
  public async flush(): Promise<void> {
    if (this.timer === undefined) {
      return;
    }
    this.cancelPending();
    await this.wipe();
  }

  private cancelPending(): void {
    if (this.timer !== undefined) {
      this.options.cancel(this.timer);
      this.timer = undefined;
    }
  }

  /** Clears only while the clipboard still holds the copied secret; unreadable or foreign content is left alone. */
  private async wipe(): Promise<void> {
    const copied = this.copied;
    this.copied = undefined;
    const clipboard = this.options.clipboard;
    if (copied === undefined || clipboard === undefined || clipboard.readText === undefined) {
      return;
    }
    try {
      if ((await clipboard.readText()) === copied) {
        await clipboard.writeText("");
      }
    } catch {
      // Read denied or clipboard taken over by another app; skip rather than wipe user content.
    }
  }
}
